<template>
  <v-app class="lightBg" :class="$route.meta.pageClass">
    <div class="calendar-loader__wrapper overlay-base" v-if="redirecting">
      <div class="calendar-loader__overlay overlay-base"></div>
      <div class="calendar-loader__content">
        <v-progress-linear indeterminate></v-progress-linear>
        <div class="text-center mt-3">Redirecting to Carrier Booking Portal...</div>
      </div>
    </div>
    <div id="app-notifications">
      <app-notification
        position="bottom center"
        class="mb-2"
        group="bottom-center-notifications"
        :max="5"></app-notification>
      <app-notification
        position="top right"
        class="mt-2 mr-2"
        group="top-right-notifications"
        :max="5"></app-notification>
    </div>
    <header class="secondary">
      <v-container fluid class="d-flex justify-space-between align-center px-12 full-height py-4">
        <template v-if="isUserAllowed && orgLoaded">
          <router-link class="header-link" :to="{ path: `/warehouse/${warehouseId}` }">
            <img :src="$logoPath" id="logo-image" alt="opendock logo" @click.stop />
          </router-link>
        </template>
        <template v-else>
          <img :src="$logoPath" id="logo-image" alt="opendock logo" />
        </template>

        <v-spacer></v-spacer>
        <template v-if="$me && isUserAllowed">
          <a
            class="header-link d-flex align-center font-weight-light mr-4"
            :href="`https://maps.google.com/?q=${$warehouseAddress}`"
            target="_blank"
            v-if="$warehouseAddress"
            rel="noopener">
            <v-icon small class="pb-1 mr-1 font-weight-light">mdi-map-marker-outline</v-icon>
            {{ $orgWarehouseName }}
          </a>
          <router-link class="header-link" :to="{ path: `/warehouse/${warehouseId}` }"
            >Home
          </router-link>
          <book-appointment-link
            :label="`${
              novaCore.isRequested($appointmentCreationStatus) ? 'Request' : 'Book'
            } Appointment`"
            class="header-link"
            :to="{ path: `/warehouse/${warehouseId}/schedule` }"></book-appointment-link>
          <router-link class="header-link" :to="{ path: `/warehouse/${warehouseId}/appointments` }"
            >My Appointments
          </router-link>
          <div class="account-nav d-flex justify-center align-center">
            <account-navigation :account-options="accountOptions"></account-navigation>
          </div>
        </template>
      </v-container>
      <div
        v-if="$isMrPreview"
        class="pa-1 text-center font-size-x-small warn font-weight-bold text-uppercase">
        Preview Mode (Read Only)
      </div>
    </header>

    <v-main>
      <v-container
        fluid
        class="pa-12 flex-grow-1 d-flex"
        :class="$route.meta.hideSidebar ? 'single-item-page' : ''">
        <div class="sidebar" v-if="!$route.meta.hideSidebar">
          <component :is="sidebarComponent"></component>
        </div>

        <div class="flex-grow-1 main-content">
          <transition name="fade" mode="out-in">
            <router-view></router-view>
          </transition>
        </div>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'App',
  computed: {
    ...mapState('navigation', {
      pageTitle: state => state.pageTitle
    }),
    ...mapState('Auth', {
      appLoaded: state => state.appLoaded
    }),
    sidebarComponent() {
      let sidebarComponentName = this.$route.meta?.title?.toLowerCase()?.split(' ')?.join('-');
      if (!sidebarComponentName) {
        sidebarComponentName = '';
      } else {
        sidebarComponentName += '-sidebar';
      }
      return sidebarComponentName;
    },
    warehouseId() {
      const paramsWarehouseId = this.$route.params.warehouseId;
      const lastViewedWarehouse = this.$loadObjectFromLocalStorage('warehouse');
      const isLastViewedWarehouseSet = Boolean(lastViewedWarehouse.warehouse?.id);
      const isParamWarehouseIdSet = paramsWarehouseId && paramsWarehouseId !== 'undefined';
      const shouldUseExistingWarehouse =
        isLastViewedWarehouseSet &&
        (!isParamWarehouseIdSet || lastViewedWarehouse.warehouse.id === paramsWarehouseId);
      return shouldUseExistingWarehouse ? lastViewedWarehouse.warehouse.id : paramsWarehouseId;
    },
    isUserAllowed() {
      return this.$store.getters['Auth/isAllowed'];
    }
  },
  data() {
    return {
      orgLoaded: false,
      accountOptions: [
        {
          title: 'Announcements',
          icon: 'bullhorn-outline',
          action: 'openBroadcastMessageDialog'
        },
        {
          title: 'Log Out',
          icon: 'logout',
          action: 'logout'
        }
      ],
      bypassCustomBookingUrl: false,
      redirecting: false
    };
  },
  methods: {
    async setWarehouse() {
      if (this.warehouseId) {
        await this.$store
          .dispatch('Warehouses/getWarehouseById', this.warehouseId)
          .then(warehouse => {
            if (warehouse) {
              if (warehouse.customBookingUrl && !this.bypassCustomBookingUrl) {
                this.redirecting = true;
                window.location = this.getCustomBookingUrl(warehouse, this.$accessToken);
              } else {
                this.$store.commit('App/setWarehouse', warehouse);
              }
            }
          })
          .finally(() => {
            this.loading = false;
          });
      }
    }
  },
  async mounted() {
    this.$store.commit('Settings/setSettingsMetadata', this.novaCore.EntitySettings);
    this.$nextTick(async () => {
      await this.$store.dispatch('Auth/setInitialAccessToken');
      await this.$store.dispatch('App/getNeutronVersion');
    });

    this.$eventHub.$on('heartbeat', payload =>
      this.$store.dispatch('App/verifyNeutronVersion', payload)
    );
  },
  created() {
    const urlParams = new URLSearchParams(window.location.search);
    const useDefaultUi = urlParams.get('defaultUi');
    this.bypassCustomBookingUrl = useDefaultUi === 'true' || useDefaultUi === '1';
  },
  watch: {
    $org() {
      if (this.$org) {
        this.orgLoaded = true;
      }
    },
    async appLoaded() {
      if (this.$route.name !== 'auth' && this.$route.meta.requiresAuth) {
        await this.setWarehouse();
      }
      if (!this.$route.meta.isAuthRequired) {
        this.$store.commit('Auth/setIsAllowed', true);
      }
      if (!this.$store.getters['Auth/isAllowed'] && this.$route.name !== 'auth') {
        this.$router.replace({ name: 'notAllowed' });
      }
    },
    async warehouseId() {
      if (this.warehouseId) {
        await this.setWarehouse();
      }
    }
  }
};
</script>

<style lang="scss">
@import '@/styles/main.scss';
.calendar-loader__wrapper {
  z-index: 10000;
}
</style>
